import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useStateContext } from '../hooks/useStateContext';
import { getStateByCode } from '../utils/states';
import { track } from '../utils/track';

// Lazy-load Stripe so it's not pulled in during build-time prerendering
// (react-snap headless rendering errors out on Stripe.js).
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = import('@stripe/stripe-js').then(m => m.loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY));
  }
  return stripePromise;
};

export default function CartPage() {
  const { items, removeFromCart, clearBundle, isBundle, total, loading, fetchCart, isGuest } = useCart();
  const navigate = useNavigate();
  const { state } = useStateContext();
  // The cart's own courses decide which state's package this is — never the browsing
  // context, so a Kansas cart is never labelled or priced as Arizona.
  const cartState = getStateByCode(items[0]?.state) || state;
  const bundlePrice = cartState.packagePrice;
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState('');

  const handleCheckout = async () => {
    track('checkout_click');
    // Guests create their account here, at checkout — their cart is kept and moved into
    // the new account, and they come straight back to this page to pay.
    if (isGuest) {
      track('signup_prompt');
      navigate('/register?next=/cart');
      return;
    }
    setCheckingOut(true);
    setError('');
    try {
      // Use bundle checkout if it's a bundle purchase
      const endpoint = isBundle ? '/bundles/checkout' : '/payments/create-checkout-session';
      const res = await api.post(endpoint);
      const stripe = await getStripe();
      track('checkout_redirect');
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        await stripe.redirectToCheckout({ sessionId: res.data.sessionId });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Checkout failed. Please try again.');
      setCheckingOut(false);
    }
  };


  if (loading) return <div className="loading-wrapper"><div className="spinner" /></div>;

  return (
    <div className="page-wrapper">
      <div className="container" style={{ maxWidth: 900 }}>
        <h1 className="page-title" style={{ marginBottom: 32 }}>Shopping Cart</h1>

        {items.length === 0 ? (
          <div style={styles.empty}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🛒</div>
            <h2 style={{ marginBottom: 8 }}>Your cart is empty</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: 24 }}>Browse our courses and add some to get started.</p>
            <Link to={`/${state.slug}/courses`} className="btn btn-primary">Browse Courses</Link>
          </div>
        ) : (
          <div style={styles.layout} className="cart-layout">
            {/* Items */}
            <div style={styles.items}>
              {isBundle ? (
                <div style={styles.cartItem}>
                  <div style={{ ...styles.itemThumb, background: 'linear-gradient(135deg, #1a3a3a, #1d4142)' }}>
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>📦</div>
                  </div>
                  <div style={styles.itemBody}>
                    <h3 style={styles.itemTitle}>Complete {cartState.name} CE Package</h3>
                    <p style={styles.itemMeta}>{items.length} courses • {items.reduce((s, i) => s + parseFloat(i.credit_hours || 0), 0)} credit hours • {cartState.name} CE</p>
                    <p style={styles.itemDesc}>All {cartState.regulator}-approved courses included — everything you need to complete your {cartState.name} real estate CE requirement.</p>
                    <div style={{ marginTop: 8 }}>
                      {items.map(item => (
                        <span key={item.id} style={{ display: 'inline-block', fontSize: 11, color: 'var(--forest-600)', background: 'var(--forest-50)', border: '1px solid var(--forest-200)', borderRadius: 999, padding: '2px 8px', marginRight: 6, marginBottom: 4 }}>{item.title}</span>
                      ))}
                    </div>
                  </div>
                  <div style={styles.itemRight}>
                    <div style={styles.itemPrice}>${bundlePrice.toFixed(2)}</div>
                    <button style={styles.removeBtn} onClick={async () => { for (const item of items) { await removeFromCart(item.course_id); } clearBundle(); await fetchCart(); }}>Remove</button>
                  </div>
                </div>
              ) : items.map(item => (
                <div key={item.id} style={styles.cartItem}>
                  <div style={styles.itemThumb}>
                    {item.thumbnail_url
                      ? <img src={item.thumbnail_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={styles.thumbPlaceholder}>📚</div>
                    }
                  </div>
                  <div style={styles.itemBody}>
                    <h3 style={styles.itemTitle}>{item.title}</h3>
                    <p style={styles.itemMeta}>{item.credit_hours} credit hours • {(getStateByCode(item.state) || cartState).name} CE</p>
                    <p style={styles.itemDesc}>{item.short_description}</p>
                  </div>
                  <div style={styles.itemRight}>
                    <div style={styles.itemPrice}>${parseFloat(item.price).toFixed(2)}</div>
                    <button style={styles.removeBtn} onClick={() => removeFromCart(item.course_id)}>Remove</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div style={styles.summary} className="cart-summary">
              <h3 style={styles.summaryTitle}>Order Summary</h3>
              {isBundle ? (
                <>
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>Complete {cartState.name} CE Package</span>
                    <span></span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                    {items.length} courses included
                  </div>
                  {total > bundlePrice && (
                    <div style={styles.summaryRow}>
                      <span style={{ ...styles.summaryLabel, textDecoration: 'line-through', color: '#999' }}>Individual total</span>
                      <span style={{ textDecoration: 'line-through', color: '#999' }}>${total.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={styles.divider} />
                  <div style={{ ...styles.summaryRow, fontWeight: 700, fontSize: 18 }}>
                    <span>Package Price</span>
                    <span style={{ color: 'var(--color-primary)' }}>${bundlePrice.toFixed(2)}</span>
                  </div>
                  {total > bundlePrice && (
                    <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, textAlign: 'center', marginTop: 4 }}>
                      You save ${(total - bundlePrice).toFixed(2)}!
                    </div>
                  )}
                </>
              ) : (
                <>
                  {items.map(item => (
                    <div key={item.id} style={styles.summaryRow}>
                      <span style={styles.summaryLabel}>{item.title}</span>
                      <span>${parseFloat(item.price).toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={styles.divider} />
                  <div style={{ ...styles.summaryRow, fontWeight: 700, fontSize: 18 }}>
                    <span>Total</span>
                    <span style={{ color: 'var(--color-primary)' }}>${total.toFixed(2)}</span>
                  </div>
                </>
              )}

              {error && <div className="alert alert-error" style={{ marginTop: 16 }}>{error}</div>}

              <button className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 20 }} onClick={handleCheckout} disabled={checkingOut}>
                {checkingOut ? 'Redirecting to Checkout...' : `Checkout — $${isBundle ? bundlePrice.toFixed(2) : total.toFixed(2)}`}
              </button>

              {isGuest && (
                <div style={{ ...styles.secureNote, color: 'var(--forest-700)', fontWeight: 600 }}>
                  Next, you'll create a free account (takes a minute) — your cart is saved.
                </div>
              )}

              <div style={styles.secureNote}>
                🔒 Secure payment via Stripe. You'll have instant access after purchase.
              </div>

              <div style={styles.continueShopping}>
                <Link to={`/${state.slug}/courses`}>← Continue Shopping</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  empty: { textAlign: 'center', padding: '80px 20px' },
  layout: { display: 'grid', gridTemplateColumns: '1fr 340px', gap: 32, alignItems: 'start' },
  items: { display: 'flex', flexDirection: 'column', gap: 16 },
  cartItem: { display: 'flex', gap: 16, padding: 20, background: 'white', border: '1px solid var(--color-border)', borderRadius: 12 },
  itemThumb: { width: 80, height: 80, borderRadius: 8, overflow: 'hidden', background: 'linear-gradient(135deg, #0f766e, #0d9488)', flexShrink: 0 },
  thumbPlaceholder: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 },
  itemBody: { flex: 1 },
  itemTitle: { fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--slate-900)' },
  itemMeta: { fontSize: 12, color: 'var(--teal-600)', fontWeight: 600, marginBottom: 6 },
  itemDesc: { fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 },
  itemRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 },
  itemPrice: { fontSize: 20, fontWeight: 800, color: 'var(--color-primary)' },
  removeBtn: { background: 'none', border: 'none', color: 'var(--color-error)', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  summary: { background: 'white', border: '1px solid var(--color-border)', borderRadius: 16, padding: 24, position: 'sticky', top: 88 },
  summaryTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 15, marginBottom: 12, gap: 16 },
  summaryLabel: { color: 'var(--slate-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  divider: { height: 1, background: 'var(--color-border)', margin: '12px 0' },
  secureNote: { fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 12, lineHeight: 1.5 },
  continueShopping: { textAlign: 'center', marginTop: 16, fontSize: 14 },
};
